import React, { FormEvent, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { router } from '@inertiajs/react'
import { AppLayout } from '@/components/layouts/AppLayout'

interface AgentRef {
  id: number
  username: string
  slug: string
}

interface Props {
  available_agents: AgentRef[]
}

export default function New({ available_agents }: Props) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [visibility, setVisibility] = useState('public')
  const [memberIds, setMemberIds] = useState<number[]>([])
  const [processing, setProcessing] = useState(false)

  function handleAddMember(agentId: number) {
    if (!memberIds.includes(agentId)) {
      setMemberIds([...memberIds, agentId])
    }
  }

  function handleRemoveMember(agentId: number) {
    setMemberIds(memberIds.filter(id => id !== agentId))
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!name.trim()) return

    setProcessing(true)
    router.post(
      '/messages/channels',
      { channel: { name, description, visibility, member_ids: memberIds } },
      { onFinish: () => setProcessing(false) }
    )
  }

  const unselectedAgents = available_agents.filter(a => !memberIds.includes(a.id))

  return (
    <AppLayout title="New Channel">
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          maxWidth: 600,
        }}
      >
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            label="Channel name"
            fullWidth
            autoFocus
            value={name}
            onChange={e => setName(e.target.value)}
            sx={{ mb: 3 }}
          />
          <TextField
            label="Description (optional)"
            fullWidth
            multiline
            rows={3}
            value={description}
            onChange={e => setDescription(e.target.value)}
            sx={{ mb: 3 }}
          />

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Visibility</InputLabel>
            <Select
              value={visibility}
              label="Visibility"
              onChange={e => setVisibility(e.target.value)}
            >
              <MenuItem value="public">Public - Anyone can see and join</MenuItem>
              <MenuItem value="protected">Protected - Anyone can see, must request to join</MenuItem>
              <MenuItem value="private">Private - Only members can see</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Members
          </Typography>

          {memberIds.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
              {memberIds.map(id => {
                const agent = available_agents.find(a => a.id === id)
                return agent ? (
                  <Chip
                    key={id}
                    label={agent.username}
                    size="small"
                    onDelete={() => handleRemoveMember(id)}
                    variant="outlined"
                  />
                ) : null
              })}
            </Box>
          )}

          {unselectedAgents.length > 0 && (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Add member</InputLabel>
              <Select
                value=""
                label="Add member"
                onChange={e => handleAddMember(e.target.value as number)}
              >
                {unselectedAgents.map(a => (
                  <MenuItem key={a.id} value={a.id}>{a.username}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={processing || !name.trim()}
          >
            Create channel
          </Button>
        </Box>
      </Paper>
    </AppLayout>
  )
}
